import { Link, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { DAYS_LONG, hoursText, openOn, place, today } from '../lib/format';
import { endsFromWaypoints, finishOf, mapEnds, tripLegs, type Leg } from '../lib/tripEnds';
import { isBar, kindLabel, SODA_NOTE, tripKindLabel } from '../lib/places';
import { useTrip } from '../state/trip';
import PlaceMap from '../components/PlaceMap';
import { ErrorNote, Spinner } from '../components/ui';

export default function SharedTripPage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const { replaceStops, update } = useTrip();
  const trip = useQuery({ queryKey: ['trip', code], queryFn: () => api.trip(code) });

  if (trip.isLoading) return <div className="mx-auto max-w-7xl px-4 sm:px-6"><Spinner label="Loading trip" /></div>;
  if (trip.error || !trip.data)
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <ErrorNote error={trip.error}>That trip link doesn&apos;t exist.</ErrorNote>
      </div>
    );

  const t = trip.data;
  const ends = endsFromWaypoints(t.waypoints);
  const legs = tripLegs(ends, t.stops);
  const pins = mapEnds(ends);
  const finish = finishOf(ends);
  const day = t.tripDate ? new Date(`${t.tripDate}T12:00:00`).getDay() : today();
  const dateText = t.tripDate
    ? new Date(`${t.tripDate}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
    : 'Date to be decided';

  return (
    <div className="mx-auto max-w-7xl px-4 pb-14 pt-10 sm:px-6">
      <p className="eyebrow">Shared {tripKindLabel(t.kind).toLowerCase()} · {dateText}</p>
      <h1 className="mt-2 text-5xl font-black sm:text-6xl">{t.name}</h1>

      {t.designatedDriver && (
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-dashed border-dd bg-dd-bg px-5 py-4">
          <span className="text-[13px] font-bold uppercase tracking-[0.1em] text-dd">Designated driver</span>
          <span className="font-display text-3xl font-black uppercase text-cream">{t.designatedDriver}</span>
          <span className="text-sm text-[#d6d2c2]">has pledged to stay sober and get everyone home.</span>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_420px]">
        <PlaceMap
          stops={t.stops}
          connectStops
          routeFrom={pins.routeFrom}
          routeTo={pins.routeTo}
          waypoints={pins.waypoints}
          fitPoints={[...t.stops.map((s) => [s.lat, s.lng] as [number, number]), ...pins.fit]}
          fitKey={t.code}
          className="h-[420px] lg:sticky lg:top-24 lg:h-[600px]"
          label={`Map of ${t.name}`}
        />

        <div className="flex flex-col gap-3">
          <ol className="flex flex-col gap-2">
            {ends.start && (
              <li className="flex flex-col gap-1">
                <EndCard tag="S" title="Start" label={ends.start.mine ? 'From wherever you are' : ends.start.label} />
                <LegText leg={legs.first} />
              </li>
            )}
            {t.stops.map((s, i) => {
              const leg = i < t.stops.length - 1 ? legs.between[i] : legs.last;
              const open = openOn(s, day);
              return (
                <li key={s.id} className="flex flex-col gap-1">
                  <div className="card flex gap-3 p-4">
                    <span className="flex size-9 flex-none items-center justify-center rounded-full bg-amber font-extrabold text-amber-ink">{i + 1}</span>
                    <div className="flex min-w-0 flex-col">
                      <Link to={`/stops/${s.slug}`} className="font-bold text-cream hover:text-amber-hi">
                        {s.name}
                      </Link>
                      <span className="text-sm text-muted">{[kindLabel(s.kind), place(s)].filter(Boolean).join(' · ')}</span>
                      <span className={`text-sm ${open === false ? 'text-warn' : 'text-[#d6d2c2]'}`}>
                        {open === false ? `Closed ${DAYS_LONG[day]}s` : hoursText(s, day)}
                      </span>
                      {t.kind !== 'BarHop' && isBar(s.kind) && <span className="text-[13px] text-dd">{SODA_NOTE}</span>}
                    </div>
                  </div>
                  <LegText leg={leg} />
                </li>
              );
            })}
            {finish && (
              <li>
                <EndCard
                  tag="F"
                  title="Finish"
                  label={ends.endAtStart ? (ends.start?.mine ? 'Back where you started' : `Back to ${ends.start!.label}`) : finish.mine ? 'Wherever you are' : finish.label}
                />
              </li>
            )}
          </ol>
          {t.notes && <p className="card p-4 text-[#d6d2c2]">{t.notes}</p>}
          <button
            type="button"
            className="btn btn-primary self-start"
            onClick={() => {
              replaceStops(t.stops, ends, t.kind ?? 'BarHop');
              // The pledge is personal: a copied bar hop keeps the driver's name but asks again.
              update({ name: t.name, driver: t.designatedDriver ?? '', date: t.tripDate ?? '', pledged: false });
              navigate('/my-trip');
            }}
          >
            Copy into my trip
          </button>
        </div>
      </div>
    </div>
  );
}

function EndCard({ tag, title, label }: { tag: string; title: string; label: string }) {
  return (
    <div className="card flex items-center gap-3 p-4">
      <span className="flex size-9 flex-none items-center justify-center rounded-md border-2 border-asphalt bg-cream font-extrabold text-asphalt" aria-hidden>
        {tag}
      </span>
      <div className="flex min-w-0 flex-col">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted">{title}</span>
        <span className="truncate font-bold text-cream">{label}</span>
      </div>
    </div>
  );
}

function LegText({ leg }: { leg: Leg | null }) {
  if (!leg) return null;
  return (
    <span className="pl-12 text-sm tabular-nums text-muted">
      {leg.walk ? `Walk ~${leg.miles.toFixed(1)} mi` : `~${Math.round(leg.miles)} mi · ~${leg.minutes} min drive`}
    </span>
  );
}
